// Story 064 — Forward-only audit log append.
//
// Every analyst mutation on the Analysis Page is recorded as a row in
// `audit_log`. Story 064 + Story 038 share this endpoint:
//   - 064 (this story) calls it from the FORWARD mutation wrappers
//     and from the Ctrl+Z handler.
//   - 038 (audit-log viewer, Phase 3 PR-J) reads from the same table.
//
// Spec: docs/backend-requirements/096-audit-log-persistence.md.
//
// FOLLOWUP(PR-D): the dev-api stub returns a synthesised uuid + the
// server clock without writing to Postgres. PR-D's audit-log writer
// replaces the stub with a real insert and tightens the failure
// surface (5xx on write failure becomes load-bearing — Ctrl+Z must
// stop progressing if the inverse mutation's audit row can't be
// recorded).
import { useMutation } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";

export type AuditEntityType =
  | "defect"
  | "aiFinding"
  | "defectLink"
  | "defectGroup"
  // PR-D addition — Story 048 lock acquire / release events.
  | "editLock";

export type AuditAction =
  | "create"
  | "update"
  | "softDelete"
  | "restore"
  | "promoteFinding"
  | "revertPromotion"
  | "linkDefects"
  | "unlinkDefects"
  | "undo"
  // PR-D additions — Story 048 + Spec 096 §"Action enum (PR-D expansion)".
  | "lock:acquire"
  | "lock:release";

export interface AuditLogAppendBody {
  entityType: AuditEntityType;
  entityId: string;
  dataAcquisitionId: string;
  action: AuditAction;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  /**
   * Required when `action === "undo"`. Identifies the audit-log row
   * this undo inverts (forward-only log; undo never deletes).
   */
  referencesAuditLogId?: string;
}

export interface AuditLogAppendResponse {
  auditLogId: string;
  timestamp: string;
}

export function useAuditLogAppendMutation() {
  const api = useApi();
  return useMutation<
    AuditLogAppendResponse,
    Error & { errorId?: string },
    AuditLogAppendBody
  >({
    mutationFn: async (body) => {
      const response = await api.post<AuditLogAppendResponse>(
        "/audit-log",
        body,
      );
      return response.data;
    },
    // No optimistic state — the audit log is downstream of every UI
    // surface (the viewer is its own tab, Story 038), so we do not
    // invalidate any queries here.
  });
}
